import { createSharedComposable } from '@vueuse/core'
import { genericOAuthClient } from 'better-auth/client/plugins'
import { createAuthClient } from 'better-auth/vue'
import { usePageContext } from 'vike-vue/usePageContext'
import { computed, reactive } from 'vue'
import { guard } from '~renderer/util/telefunc'
import { onSignoutUser } from '~rpc/procedures/user/onSignoutUser.telefunc'
import { PATREON_TIER_LOOKUP } from '~shared/config/patreon'
import type { SocialProviderId } from '~shared/types/auth'
import * as AttemptUtil from '~shared/util/attempt'
import { isAnonymousUser } from '~shared/util/user'

export const useAuth = createSharedComposable(() => {
  const authClient = createAuthClient({
    plugins: [genericOAuthClient()],
  })
  const pageContext = usePageContext()

  // MARK: Computed
  const user = computed(() => pageContext.user)
  const _accounts = computed(() => {
    if (!user.value) return []
    if (isAnonymousUser(user.value)) return []

    return user.value.accounts
  })
  const accounts = reactive({
    patreon: computed(() => _accounts.value.some((a) => a.providerId === 'patreon')),
    twitch: computed(() => _accounts.value.some((a) => a.providerId === 'twitch')),
    discord: computed(() => _accounts.value.some((a) => a.providerId === 'discord')),
  })
  const hasLinkedAccounts = computed(() => _accounts.value.length > 1)
  const requiresUsernameReset = computed(
    () => user.value && !isAnonymousUser(user.value) && user.value.usernameResetRequired,
  )

  const hasPatreonTier = (level: number) => {
    // return true //uncomment for testing
    if (!user.value) return false
    if (isAnonymousUser(user.value)) return false
    if (isUnlocked.value) return true
    if (!user.value.patreon) return false

    const { tierId } = user.value.patreon
    const tier = PATREON_TIER_LOOKUP[tierId] ?? 0

    return tier >= level
  }

  const isAdmin = computed(() => {
    if (!user.value) return false
    if (isAnonymousUser(user.value)) return false

    return user.value.roles?.includes('admin')
  })

  const isModerator = computed(() => {
    if (!user.value) return false
    if (isAnonymousUser(user.value)) return false

    return user.value.roles?.includes('moderator')
  })

  const isUnlocked = computed(() => {
    if (!user.value) return false
    if (isAnonymousUser(user.value)) return false

    return user.value.roles?.includes('unlocked')
  })

  const isBanned = computed(() => {
    if (!user.value) return false
    if (isAnonymousUser(user.value)) return false

    return user.value.type === 'banned'
  })

  const isAnonymous = computed(() => {
    if (!user.value) return false
    return isAnonymousUser(user.value)
  })

  // MARK: Methods
  const signIn = async (provider: SocialProviderId) => {
    if (provider === 'patreon') {
      await authClient.signIn.oauth2({
        providerId: 'patreon',
        callbackURL: '/',
      })
      return
    }
    await authClient.signIn.social({
      provider,
      callbackURL: '/',
    })
  }

  const linkAccount = async (provider: SocialProviderId) => {
    if (provider === 'patreon') {
      await authClient.oauth2.link({
        providerId: 'patreon',
        callbackURL: '/user',
      })
      return
    }
    await authClient.linkSocial({
      provider,
      callbackURL: '/user',
    })
  }

  const unlinkAccount = async (provider: SocialProviderId) => {
    if (!hasLinkedAccounts) return

    await authClient.unlinkAccount({
      providerId: provider,
    })
    window.location.reload()
  }

  const signOut = async () => {
    if (!user.value) return

    const [err] = await AttemptUtil.async(() => guard(onSignoutUser()))
    if (err) return

    if (isAnonymousUser(user.value)) {
      window.location.href = '/api/auth/anonymous/signout'
      return
    }

    await authClient.signOut()
    window.location.href = '/'
  }

  return {
    user,
    hasLinkedAccounts,
    requiresUsernameReset,
    accounts,
    hasPatreonTier,
    isAdmin,
    isAnonymous,
    isModerator,
    isBanned,
    isUnlocked,
    signIn,
    signOut,
    linkAccount,
    unlinkAccount,
  }
})
